const INTEGER_FORMATTER = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

const DECIMAL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

/** Units of measure used by the catalog. Kept as a literal union so the API mapping is checked. */
export const MEASUREMENT_UNITS = [
  'un',
  'cx',
  'fd',
  'pct',
  'sc',
  'dz',
  't',
  'kg',
  'g',
  'L',
  'mL',
] as const;

export type MeasurementUnit = (typeof MEASUREMENT_UNITS)[number];

/**
 * Symbol of each reference unit by its code (migration `0029_...`). An order line freezes the
 * unit's *code* ("KG") at placement, and the screen prints its symbol ("kg") — this is how,
 * without a round trip for a list that only grows when Instoque registers a unit. A code not
 * listed here (one an operator registered later) falls back to its lower case.
 */
const SYMBOLS_BY_CODE: Readonly<Record<string, MeasurementUnit>> = {
  UN: 'un',
  CX: 'cx',
  FD: 'fd',
  PCT: 'pct',
  SC: 'sc',
  DZ: 'dz',
  T: 't',
  KG: 'kg',
  G: 'g',
  L: 'L',
  ML: 'mL',
};

/** `KG` → `kg`, `ML` → `mL`, `T` → `t`. */
export function unitSymbolForCode(code: string): string {
  return SYMBOLS_BY_CODE[code.toUpperCase()] ?? code.toLowerCase();
}

/** Plain quantity with pt-BR grouping: `1.250`. */
export function formatQuantity(value: number): string {
  return Number.isInteger(value)
    ? INTEGER_FORMATTER.format(value)
    : DECIMAL_FORMATTER.format(value);
}

/** Quantity with its unit: `12 cx`, `1,5 t`, `250 kg`. An empty unit prints the number alone. */
export function formatQuantityWithUnit(
  value: number,
  unit: MeasurementUnit | (string & {}) | undefined,
): string {
  return unit === undefined || unit === ''
    ? formatQuantity(value)
    : `${formatQuantity(value)} ${unit}`;
}

/** `2 caixas de 12 un` — how a purchasing unit is described in the catalog and cart. */
export function formatPackaging(
  packageQuantity: number,
  itemsPerPackage: number,
  unit: MeasurementUnit,
): string {
  return `${formatQuantity(packageQuantity)} × ${formatQuantityWithUnit(itemsPerPackage, unit)}`;
}
